import React, { useState, useEffect, useRef } from "react";

const RoundTableCardGame = () => {
  const [players, setPlayers] = useState([]);
  const [gameStarted, setGameStarted] = useState(false);
  const [gameEnded, setGameEnded] = useState(false);
  const [winner, setWinner] = useState(null);
  const [instantWin, setInstantWin] = useState(false);
  const [round, setRound] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [numPlayers, setNumPlayers] = useState(11);
  const [layoutMode, setLayoutMode] = useState("circle"); // 'circle' or 'horizontal'
  const [cardOrientation, setCardOrientation] = useState("angled"); // 'vertical','horizontal','angled'
  const [screenWidth, setScreenWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200
  );

  const horizontalContainerRef = useRef(null);

  const cards = [
    { suit: "♠", value: "A", number: 1 },
    { suit: "♠", value: "2", number: 2 },
    { suit: "♠", value: "3", number: 3 },
    { suit: "♠", value: "4", number: 4 },
    { suit: "♠", value: "5", number: 5 },
    { suit: "♠", value: "6", number: 6 },
    { suit: "♠", value: "7", number: 7 },
    { suit: "♠", value: "8", number: 8 },
    { suit: "♠", value: "9", number: 9 },
    { suit: "♠", value: "10", number: 10 },
    { suit: "♠", value: "J", number: 11 },
    { suit: "♥", value: "A", number: 1 },
    { suit: "♥", value: "2", number: 2 },
    { suit: "♥", value: "3", number: 3 },
    { suit: "♥", value: "4", number: 4 },
    { suit: "♥", value: "5", number: 5 },
    { suit: "♥", value: "6", number: 6 },
    { suit: "♥", value: "7", number: 7 },
    { suit: "♥", value: "8", number: 8 },
    { suit: "♥", value: "9", number: 9 },
    { suit: "♥", value: "10", number: 10 },
    { suit: "♥", value: "J", number: 11 },
  ];

  useEffect(() => {
    const onResize = () => setScreenWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (gameStarted && players.length > 0 && numPlayers < players.length) {
      const np = players.slice(0, numPlayers);
      setPlayers(np);
      if (winner > numPlayers) {
        setWinner(null);
        setGameEnded(false);
      }
    }
  }, [numPlayers]);

  // 当切换到横向模式时，强制重新渲染以正确计算布局
  useEffect(() => {
    if (layoutMode === "horizontal" && gameStarted) {
      // 延迟一小段时间确保容器尺寸已更新
      const timer = setTimeout(() => {
        setPlayers([...players]);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [layoutMode]);

  const checkForWinner = (arr) => {
    for (let i = 0; i < arr.length; i++) {
      if (arr[i].cards[0].number === arr[i].cards[1].number) {
        return { hasWinner: true, winnerIndex: i + 1 };
      }
    }
    return { hasWinner: false, winnerIndex: null };
  };

  const dealCards = () => {
    setIsAnimating(true);
    setInstantWin(false);
    let np = Array(numPlayers)
      .fill()
      .map(() => ({ cards: [], passingCard: null }));

    // 创建牌堆，确保有足够的不同数字
    let deck = [];
    for (let i = 0; i < numPlayers; i++) {
      const i1 = Math.min(i, cards.length - 1),
        i2 = Math.min(i + 11, cards.length - 1);
      deck.push(cards[i1], cards[i2]);
    }

    // 使用更智能的发牌策略，避免开局就有相同数字的牌
    const maxAttempts = 100;
    let attempts = 0;
    let validDeal = false;

    while (!validDeal && attempts < maxAttempts) {
      attempts++;

      // 洗牌
      deck.sort(() => Math.random() - 0.5);

      // 尝试发牌
      let tempPlayers = Array(numPlayers)
        .fill()
        .map(() => ({ cards: [] }));

      validDeal = true;
      for (let i = 0; i < numPlayers; i++) {
        tempPlayers[i].cards = [deck[i * 2], deck[i * 2 + 1]];
        // 检查是否有相同数字
        if (tempPlayers[i].cards[0].number === tempPlayers[i].cards[1].number) {
          validDeal = false;
          break;
        }
      }

      if (validDeal) {
        // 成功找到一个没有开局获胜的发牌方案
        for (let i = 0; i < numPlayers; i++) {
          np[i].cards = tempPlayers[i].cards;
        }
      }
    }

    // 如果多次尝试都失败了，就使用最后一次的结果（可能有开局获胜）
    if (!validDeal) {
      for (let i = 0; i < numPlayers; i++) {
        np[i].cards = [deck[i * 2], deck[i * 2 + 1]];
      }
    }

    const { hasWinner, winnerIndex } = checkForWinner(np);
    setPlayers(np);
    setGameStarted(true);
    setGameEnded(hasWinner);
    setWinner(winnerIndex);
    if (hasWinner) setInstantWin(true);
    setRound(0);
    setTimeout(() => setIsAnimating(false), 500);
  };

  const organizeCards = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    const np = [...players];
    np.forEach((p) => {
      if (p.cards[0].number > p.cards[1].number) {
        p.cards = [p.cards[1], p.cards[0]];
      }
    });
    setPlayers([...np]);
    setTimeout(() => setIsAnimating(false), 500);
  };

  const swapCards = () => {
    if (isAnimating || gameEnded) return;
    setIsAnimating(true);
    const np = [...players];
    const toPass = [];
    for (let i = 0; i < numPlayers; i++) {
      const p = np[i];
      if (p.cards[0].number <= p.cards[1].number) {
        p.passingCard = 0;
        toPass[i] = p.cards[0];
      } else {
        p.passingCard = 1;
        toPass[i] = p.cards[1];
      }
    }
    setPlayers([...np]);
    setTimeout(() => {
      for (let i = 0; i < numPlayers; i++) {
        const p = np[i];
        const rec = toPass[(i + numPlayers - 1) % numPlayers];
        if (p.passingCard === 0) p.cards = [rec, p.cards[1]];
        else p.cards = [p.cards[0], rec];
        p.passingCard = null;
      }
      const { hasWinner, winnerIndex } = checkForWinner(np);
      setPlayers([...np]);
      setRound((r) => r + 1);
      setGameEnded(hasWinner);
      setWinner(winnerIndex);
      setIsAnimating(false);
    }, 800);
  };

  return (
    <div className="flex flex-col items-center p-4 max-w-6xl mx-auto font-sans">
      <h1 className="text-3xl font-bold mb-4 text-blue-700">圆桌换牌游戏</h1>

      {gameStarted && (
        <div className="mb-4 text-lg">
          第{round}轮
          {gameEnded && (
            <span className="font-bold text-green-600 ml-2">游戏结束啦！</span>
          )}
          {instantWin && (
            <span className="font-bold text-purple-600 ml-2">开局就赢了！</span>
          )}
        </div>
      )}

      <div className="flex flex-wrap gap-4 mb-4">
        <div className="flex items-center">
          <label htmlFor="numPlayers" className="mr-2">
            玩家人数：
          </label>
          <input
            type="range"
            id="numPlayers"
            min="2"
            max="11"
            value={numPlayers}
            onChange={(e) => setNumPlayers(+e.target.value)}
            className="mr-2"
          />
          <span className="font-medium text-blue-600">{numPlayers}人</span>
        </div>

        <div className="flex items-center">
          <label className="mr-2">布局方式：</label>
          <select
            value={layoutMode}
            onChange={(e) => setLayoutMode(e.target.value)}
            className="px-2 py-1 border border-gray-300 rounded"
          >
            <option value="circle">圆桌</option>
            <option value="horizontal">横排</option>
          </select>
        </div>

        {layoutMode === "circle" && (
          <div className="flex items-center">
            <label className="mr-2">卡牌摆放：</label>
            <select
              value={cardOrientation}
              onChange={(e) => setCardOrientation(e.target.value)}
              className="px-2 py-1 border border-gray-300 rounded"
            >
              <option value="vertical">竖着放</option>
              <option value="horizontal">横着放</option>
              <option value="angled">斜着放</option>
            </select>
          </div>
        )}
      </div>

      {layoutMode === "circle" ? (
        <div className="relative w-full aspect-square max-w-xl rounded-full border-4 border-gray-800 mb-8 flex items-center justify-center bg-green-50">
          {/* <div className="absolute text-lg font-medium text-blue-700">
            圆桌换牌（向右传牌）
          </div> */}
          {players.map((player, index) => {
            const pIdx = numPlayers - index - 1;
            const angle = (pIdx / numPlayers) * 2 * Math.PI;
            const x = Math.cos(angle - Math.PI / 2),
              y = Math.sin(angle - Math.PI / 2);

            // 圆桌坐标
            const R = 42;
            const left = `calc(50% + ${R}% * ${x})`;
            const top = `calc(50% + ${R}% * ${y})`;

            // 编号位置
            const r2 = 30;
            const nLeft = `calc(50% + ${r2}% * ${x})`;
            const nTop = `calc(50% + ${r2}% * ${y})`;

            const wonP = winner === index + 1;

            // 传牌向量
            const nxt = (pIdx - 1 + numPlayers) % numPlayers;
            const a2 = (nxt / numPlayers) * 2 * Math.PI;
            const x2 = Math.cos(a2 - Math.PI / 2),
              y2 = Math.sin(a2 - Math.PI / 2);
            const dx = x2 - x,
              dy = y2 - y;
            const dist = Math.hypot(dx, dy),
              md = 35;
            const vx = (dx / dist) * md,
              vy = (dy / dist) * md;

            const isH = cardOrientation === "horizontal";

            return (
              <div key={index}>
                {/* 玩家编号 */}
                <div
                  className={`
                    absolute w-8 h-8 flex items-center justify-center rounded-full font-bold text-lg z-10
                    ${
                      wonP
                        ? "bg-green-300 border-4 border-green-600 scale-125"
                        : "bg-blue-100 border-2 border-blue-500"
                    }
                  `}
                  style={{
                    left: nLeft,
                    top: nTop,
                    transform: "translate(-50%,-50%)",
                    boxShadow: wonP ? "0 0 10px #22c55e" : "",
                  }}
                >
                  {index + 1}
                </div>

                {/* 扑克牌 */}
                <div
                  className="absolute"
                  style={{ left, top, transform: "translate(-50%,-50%)" }}
                >
                  <div className="flex flex-col items-center">
                    <div
                      className={`flex ${
                        isH ? "flex-row space-x-1" : "flex-col space-y-1"
                      } items-center mt-4`}
                    >
                      {player.cards.map((card, ci) => {
                        const passing = player.passingCard === ci;
                        const tx = passing ? vx : 0;
                        const ty = passing ? vy : 0;
                        const deg = isH
                          ? 0
                          : cardOrientation === "angled"
                          ? angle * (180 / Math.PI) + 180
                          : 0;
                        const wonC =
                          wonP &&
                          player.cards[0].number === player.cards[1].number;

                        return (
                          <div
                            key={ci}
                            className={`
                              border-2 rounded flex justify-center items-center bg-white shadow-md
                              transition-transform duration-500
                              ${
                                card.suit === "♥"
                                  ? "text-red-600"
                                  : "text-black"
                              }
                              ${
                                wonC
                                  ? "border-green-500 border-4"
                                  : "border-gray-800"
                              }
                            `}
                            style={{
                              width: isH ? "35px" : "30px",
                              height: isH ? "30px" : "35px",
                              fontSize: "0.825rem",
                              transform: `translate(${tx}px, ${ty}px) rotate(${deg}deg)`,
                              opacity: passing ? 0.6 : 1,
                              boxShadow: wonC ? "0 0 15px #22c55e" : "",
                              animation: wonC
                                ? instantWin
                                  ? "spinAndPulse 1.2s infinite"
                                  : "pulse 1.5s infinite"
                                : "none",
                            }}
                          >
                            <span>
                              {card.suit}
                              {card.value}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        // 横向模式：借鉴第二个代码的布局思路，但保持第一个代码的样式风格
        <div
          className="w-full mb-8 overflow-hidden"
          ref={horizontalContainerRef}
        >
          <div
            className="flex flex-wrap justify-start items-end bg-green-50 border-4 border-gray-800 rounded-lg p-4 w-full"
            style={{ minHeight: "300px" }}
          >
            {(() => {
              // 计算可用宽度
              const cw =
                horizontalContainerRef.current?.clientWidth ??
                window.innerWidth;
              const padding = 32;
              const availW = cw - padding;

              // 检查是否需要分两行
              const unitCnt1 = numPlayers * 2 - 1;
              const cardW1 = Math.floor(availW / unitCnt1);
              const MIN_CARD = 40;
              const use2Rows = cardW1 < MIN_CARD;

              // 计算每行玩家数和实际卡宽
              const perRow = use2Rows ? Math.ceil(numPlayers / 2) : numPlayers;
              const unitCnt = perRow * 2 - 1;
              const cardW = Math.min(Math.floor(availW / unitCnt), 120); // 限制最大宽度为120px
              const cardH = Math.floor(cardW * 1.5);

              // 分行处理
              const rows = use2Rows
                ? [players.slice(0, perRow), players.slice(perRow)]
                : [players];

              return rows.map((rowPlayers, rowIdx) => (
                <div
                  key={rowIdx}
                  className={`flex items-end justify-center w-full ${
                    rowIdx > 0 ? "mt-2" : ""
                  }`}
                >
                  {rowPlayers.map((player, i) => {
                    const idx = rowIdx * perRow + i;
                    const wonP = winner === idx + 1;
                    const isFirst = i === 0;
                    const mLeft = isFirst ? 0 : cardW;

                    return (
                      <div
                        key={idx}
                        className="flex flex-col items-center"
                        style={{
                          marginLeft: `${mLeft}px`,
                        }}
                      >
                        {/* 编号 */}
                        <div
                          className={`
                            w-8 h-8 flex items-center justify-center rounded-full font-bold text-sm mb-1.5
                            ${
                              wonP
                                ? "bg-green-300 border-4 border-green-600 scale-125"
                                : "bg-blue-100 border-2 border-blue-500"
                            }
                          `}
                          style={{ boxShadow: wonP ? "0 0 10px #22c55e" : "" }}
                        >
                          {idx + 1}
                        </div>

                        {/* 牌 */}
                        <div className="flex flex-col items-center">
                          {player.cards.map((card, ci) => {
                            const passing = player.passingCard === ci;
                            const shiftX = passing ? cardW : 0;
                            const wonC =
                              wonP &&
                              player.cards[0].number === player.cards[1].number;

                            return (
                              <div
                                key={ci}
                                className={`
                                  border-2 rounded flex justify-center items-center bg-white shadow-md
                                  transition-transform duration-500
                                  ${
                                    card.suit === "♥"
                                      ? "text-red-600"
                                      : "text-black"
                                  }
                                  ${
                                    wonC
                                      ? "border-green-500 border-4"
                                      : "border-gray-800"
                                  }
                                `}
                                style={{
                                  width: `${cardW}px`,
                                  height: `${cardH}px`,
                                  fontSize: `${(cardW * 0.36).toFixed(1)}px`,
                                  margin: "4px 0",
                                  transform: `translateX(${shiftX}px)`,
                                  opacity: passing ? 0.6 : 1,
                                  boxShadow: wonC ? "0 0 15px #22c55e" : "",
                                  animation: wonC
                                    ? instantWin
                                      ? "spinAndPulse 1.2s infinite"
                                      : "pulse 1.5s infinite"
                                    : "none",
                                }}
                              >
                                <span>
                                  {card.suit}
                                  {card.value}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ));
            })()}
          </div>
        </div>
      )}

      {/* 操作按钮 */}
      <div className="flex flex-wrap justify-center mt-4 gap-2">
        {!gameStarted ? (
          <button
            className="px-4 py-2 text-lg bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors font-medium"
            onClick={dealCards}
            disabled={isAnimating}
          >
            开始发牌
          </button>
        ) : (
          <>
            <button
              className={`px-4 py-2 text-lg bg-blue-500 text-white rounded-lg transition-colors font-medium ${
                gameEnded || isAnimating
                  ? "opacity-50 cursor-not-allowed"
                  : "hover:bg-blue-600"
              }`}
              onClick={swapCards}
              disabled={gameEnded || isAnimating}
            >
              传牌
            </button>
            {layoutMode === "horizontal" && (
              <button
                className={`px-4 py-2 text-lg bg-purple-500 text-white rounded-lg transition-colors font-medium ${
                  gameEnded || isAnimating
                    ? "opacity-50 cursor-not-allowed"
                    : "hover:bg-purple-600"
                }`}
                onClick={organizeCards}
                disabled={gameEnded || isAnimating}
              >
                整理
              </button>
            )}
            <button
              className={`px-4 py-2 text-lg bg-green-500 text-white rounded-lg transition-colors font-medium ${
                isAnimating
                  ? "opacity-50 cursor-not-allowed"
                  : "hover:bg-green-600"
              }`}
              onClick={dealCards}
              disabled={isAnimating}
            >
              重新开始
            </button>
          </>
        )}
      </div>

      {/* 胜利提示 */}
      {gameEnded && (
        <div
          className={`text-xl font-bold mt-4 text-green-600 ${
            instantWin ? "animate-pulse" : "animate-bounce"
          }`}
        >
          恭喜！第{winner}号玩家获胜！
          {instantWin && <span className="text-purple-600"> 开局就赢啦</span>}
        </div>
      )}

      {/* 游戏规则 */}
      <div className="mt-8 text-gray-700 p-4 bg-blue-50 rounded-lg max-w-lg border border-blue-200">
        <h2 className="text-xl font-bold mb-3 text-blue-700">游戏玩法</h2>
        <div className="space-y-2 text-base">
          <p>
            1. {numPlayers}个小朋友坐成
            {layoutMode === "circle" ? "一个圆圈" : "一排"}，
            每人拿2张牌（牌上有A、2、3...到J）
          </p>
          <p>2. 每人把数字小的那张牌传给右边的小朋友</p>
          <p>3. 如果谁的两张牌数字一样，谁就获胜了！</p>
          <p>4. 思考题：不管怎么发牌，这个游戏都能结束吗？</p>
        </div>
      </div>

      <style jsx>{`
        @keyframes pulse {
          0% {
            box-shadow: 0 0 5px #22c55e;
          }
          50% {
            box-shadow: 0 0 20px #22c55e;
          }
          100% {
            box-shadow: 0 0 5px #22c55e;
          }
        }
        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
        @keyframes spinAndPulse {
          0% {
            box-shadow: 0 0 5px #22c55e;
            transform: rotate(0deg) scale(1);
          }
          50% {
            box-shadow: 0 0 25px #22c55e;
            transform: rotate(180deg) scale(1.1);
          }
          100% {
            box-shadow: 0 0 5px #22c55e;
            transform: rotate(360deg) scale(1);
          }
        }
      `}</style>
      <footer className="mt-8 w-full text-center text-sm text-gray-500">
        © {new Date().getFullYear()} 孙维刚教育研究院-陈硕老师 保留所有权利。
      </footer>
    </div>
  );
};

export default RoundTableCardGame;
